import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { ArrowRight, Shield, Car, ExternalLink, Play, Volume2, VolumeX } from "lucide-react";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";

const BBMC_LINK = "https://www.blackboxmycar.com/products/ownli-dash-cam-recommendations";
const BBMC_LOGO = "https://parked.blob.core.windows.net/uploads/blackboxmycar.png";
const BBMC_ICON = "https://parked.blob.core.windows.net/ownli/bbmc_transparent_rounded_border.png";
const BBMC_VIDEO = "https://cdn.shopify.com/videos/c/vp/91af07a6b11049aea85a188217774113/91af07a6b11049aea85a188217774113.HD-1080p-7.2Mbps-10154113.mp4";

const BlackboxBanner = ({ trackButtonClick }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const handlePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleShopNow = () => {
    trackButtonClick("bbmc-shop-now", {
      step_name: "insurance_alternatives",
    });
    window.open(BBMC_LINK, "_blank");
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm">
      {/* Video section */}
      <div className="relative bg-black cursor-pointer" onClick={handlePlay}>
        <video
          ref={videoRef}
          src={BBMC_VIDEO}
          muted={isMuted}
          playsInline
          preload="metadata"
          className="w-full aspect-video object-cover"
          onEnded={() => setIsPlaying(false)}
        />
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
              <Play className="w-6 h-6 text-gray-900 ml-1" />
            </div>
          </div>
        )}
        {isPlaying && (
          <button
            onClick={toggleMute}
            className="absolute bottom-3 right-3 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center"
          >
            {isMuted ? (
              <VolumeX className="w-4 h-4 text-white" />
            ) : (
              <Volume2 className="w-4 h-4 text-white" />
            )}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <img
            src={BBMC_ICON}
            alt="Blackboxmycar"
            className="w-10 h-10 rounded-lg flex-shrink-0"
          />
          <div>
            <p className="text-xs uppercase tracking-wide font-bold text-gray-400">
              Recommended by Ownli
            </p>
            <p className="font-bold text-gray-900">Blackboxmycar</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed">
          North America's #1 largest authorized dash cam supplier.
          Protect yourself on the road with top quality dash cams at the best prices.
        </p>

        <img
          src={BBMC_LOGO}
          alt="Blackboxmycar"
          className="w-full rounded-lg"
        />

        <button
          onClick={handleShopNow}
          className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-3 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold uppercase shadow-lg shadow-[#7B61FF]/30"
        >
          <span>Shop Dash Cams</span>
          <ExternalLink className="w-4 h-4" />
        </button>

        <p className="text-xs text-center text-gray-400">
          Curated picks from Ownli's partner network
        </p>
      </div>
    </div>
  );
};

const OfferDeclineChoiceScreen = ({ onOptionSelected }) => {
  const { trackButtonClick } = useInteractionTracking();

  useEffect(() => {
    analyticsService.trackPageView("insurance_alternatives");
  }, []);

  const handleOptionClick = (option) => {
    trackButtonClick("alternative-option", {
      step_name: "insurance_alternatives",
      option_selected: option,
    });
    onOptionSelected(option);
  };

  const options = [
    {
      id: "extended_warranty",
      label: "EXTENDED WARRANTY",
      description: "Protect your vehicle with an extended warranty plan",
      icon: Shield,
    },
    {
      id: "refinancing",
      label: "AUTO REFINANCING",
      description: "Lower your monthly car payment with better rates",
      icon: Car,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-xl font-bold uppercase">
          Thanks For Your Interest!
        </h1>
        <p className="text-darkGrey">
          No worries — check out these other ways Ownli can help you save.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-3">
          {options.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                onClick={() => handleOptionClick(option.id)}
                className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-purpleCore" />
                  <div className="text-left">
                    <span className="font-bold text-gray-900 block">
                      {option.label}
                    </span>
                    <span className="text-sm text-darkGrey">
                      {option.description}
                    </span>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Blackboxmycar Partner Banner */}
      <BlackboxBanner trackButtonClick={trackButtonClick} />
    </div>
  );
};

export default OfferDeclineChoiceScreen;
